import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { caseStudies } from "../constant/caseStudies";
import { ClientLogo } from "./ClientLogo";
import "./CaseStudies.css";

const ImageWithPlaceholder = ({
  src,
  alt,
  className = "",
  style = {},
  placeholderTitle = "Loading Image...",
  placeholderHint = "",
  placeholderStyle = {},
  iconClass = "fa-regular fa-image",
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = React.useRef(null);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth > 0) {
        setLoaded(true);
      }
    }
  }, [src]);

  return (
    <div className="position-relative w-100">
      {(!loaded || error || !src) && (
        <div className="pdf-image-placeholder" style={placeholderStyle}>
          <i className={`${iconClass} pdf-image-placeholder-icon`}></i>
          <div className="pdf-image-placeholder-title">{placeholderTitle}</div>
          {placeholderHint && (
            <div className="pdf-image-placeholder-hint">{placeholderHint}</div>
          )}
        </div>
      )}
      {src && !error && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`${className} ${loaded ? "d-block" : "d-none"}`}
          style={{ ...style, width: "100%" }}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
    </div>
  );
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const foundStudy = caseStudies.find(
      (cs) => cs.slug === slug || String(cs.id) === slug
    );

    if (foundStudy) {
      setStudy(foundStudy);
      document.title =
        foundStudy.meta?.title || `${foundStudy.client} Case Study | Tedekstra`;

      let metaTag = document.querySelector("meta[name='description']");
      const description =
        foundStudy.meta?.description || foundStudy.clientOverview[0];
      if (metaTag) {
        metaTag.setAttribute("content", description);
      } else {
        metaTag = document.createElement("meta");
        metaTag.name = "description";
        metaTag.content = description;
        document.head.appendChild(metaTag);
      }
    }

    if (window.AOS) {
      window.AOS.refresh();
    }
  }, [slug]);

  if (!study) {
    return (
      <div className="container py-5 text-center my-5">
        <h2>Case Study Not Found</h2>
        <p className="text-muted">The requested case study could not be located.</p>
        <Link
          to="/case-studies"
          className="btn btn-dark rounded-pill px-4 py-2 mt-3"
        >
          Back to Case Studies
        </Link>
      </div>
    );
  }

  const currentIndex = caseStudies.findIndex(
    (cs) => cs.slug === study.slug || String(cs.id) === String(study.id)
  );
  const nextStudy = caseStudies[(currentIndex + 1) % caseStudies.length];

  // Check if this is the specialized 5-sheet Nail Tech Rich study
  const isNailTechRich = study.slug === "nail-tech-rich";

  return (
    <div className="pdf-view-wrapper">
      <div className="container" style={{ maxWidth: "1000px" }}>
        {/* Top Control Bar */}
        <div className="pdf-control-bar">
          <Link to="/case-studies" className="pdf-back-btn">
            <i className="fa-solid fa-arrow-left"></i> All Case Studies
          </Link>

          <div className="pdf-btn-actions">
            <button
              type="button"
              onClick={() => window.print()}
              className="pdf-download-btn"
              style={{
                marginRight: "10px",
                background: "#f1f5f9",
                color: "#1e293b",
                border: "1px solid #cbd5e1",
              }}
              title="Print or Save as PDF"
            >
              <i className="fa-solid fa-print"></i> Print / Save as PDF
            </button>

            <a
              href={study.pdfUrl}
              download={`${study.slug}-case-study.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-download-btn"
              style={{ marginRight: "14px" }}
            >
              <i className="fa-regular fa-file-pdf"></i> Download Official PDF
            </a>

            {nextStudy && (
              <Link
                to={`/case-studies/${nextStudy.slug}`}
                className="pdf-switch-btn"
              >
                <span>Next: {nextStudy.client}</span>{" "}
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            )}
          </div>
        </div>

        {/* =========================================================
            NAIL TECH RICH 5-SHEET PRESENTATION LAYOUT
           ========================================================= */}
        {isNailTechRich ? (
          <>
            {/* Sheet 1: Hero, Overview, The Challenge & Approach */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 1 / 5</div>

              {/* Header with Logo */}
              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              {/* Green Hero Box */}
              <div className="pdf-green-hero">
                <div className="row align-items-center">
                  <div className="col-md-7">
                    <div className="pdf-hero-headline">CASE STUDY</div>
                    <div className="pdf-hero-client">{study.client}</div>
                    <div className="pdf-hero-project">{study.subtitle}</div>
                  </div>
                  <div className="col-md-5 mt-3 mt-md-0">
                    <div className="pdf-hero-img-box">
                      <img
                        src={study.image || "/images/nail_tech_rich_case_study_compact.jpg"}
                        alt={study.client}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Overview */}
              <div className="row align-items-center mb-4">
                <div className="col-md-8">
                  <h2 className="pdf-section-title">Project Overview</h2>
                  {study.clientOverview.map((para, idx) => (
                    <p key={idx} className="pdf-body-paragraph">
                      {para}
                    </p>
                  ))}
                </div>
                <div className="col-md-4 d-flex justify-content-md-end justify-content-center mt-3 mt-md-0">
                  <ClientLogo type={study.logoType} />
                </div>
              </div>

              {/* The Challenge */}
              <div className="pdf-outlined-frame mb-4">
                <div className="pdf-floating-pill">The Challenge</div>
                <p className="pdf-frame-intro-text">{study.challengeIntro}</p>

                <div className="pdf-2col-chevrons">
                  {study.challenges.map((ch, idx) => (
                    <div className="pdf-chevron-row" key={idx}>
                      <span className="pdf-chevron-circle pdf-circle-green">
                        <i className="fa-solid fa-chevron-right"></i>
                      </span>
                      <span>{ch}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Our Approach */}
              <div className="mt-4 pt-2">
                <h2
                  className="pdf-section-title"
                  style={{ color: "#2563eb", marginBottom: "8px" }}
                >
                  Our Approach
                </h2>
                <p className="pdf-body-paragraph">{study.ourApproach}</p>
              </div>
            </div>

            {/* Sheet 2: Structured Key Areas, The Solution & Banner */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 2 / 5</div>

              {/* Header with Logo */}
              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              {/* Experience Structure (5 Key Areas) */}
              <div className="mb-4">
                <p
                  className="pdf-body-paragraph font-weight-bold"
                  style={{ fontSize: "1.05rem", marginBottom: "16px" }}
                >
                  {study.experienceStructureIntro}
                </p>

                <div className="pdf-key-areas-list">
                  {study.keyAreas.map((area, idx) => (
                    <div className="pdf-key-area-pill-row" key={idx}>
                      <span className="pdf-key-area-badge">{area.badge}</span>
                      <p className="pdf-key-area-text">{area.description}</p>
                    </div>
                  ))}
                </div>

                <p className="pdf-body-paragraph mt-3 text-muted">
                  {study.keyAreasOutro}
                </p>
              </div>

              {/* The Solution */}
              <div className="pdf-outlined-frame mb-4">
                <div className="pdf-floating-pill">The Solution</div>
                <p
                  className="pdf-frame-intro-text font-weight-bold"
                  style={{
                    fontSize: "1.05rem",
                    color: "#111827",
                    marginBottom: "12px",
                  }}
                >
                  {study.solutionIntro}
                </p>

                {study.solutionBody && (
                  <p className="pdf-body-paragraph mb-3">
                    {study.solutionBody}
                  </p>
                )}

                <p className="pdf-frame-outro-text mb-0">{study.solutionOutro}</p>
              </div>

              {/* Mid-Page Banner (with loading placeholder fallback) */}
              <div className="text-center my-3" style={{ overflow: "hidden", borderRadius: "10px" }}>
                <ImageWithPlaceholder
                  src="/images/Nail-Tech-Bar.png"
                  alt="Nail Tech Rich - Pricing, Booking & Marketing"
                  placeholderTitle="Nail Tech Rich Banner"
                  placeholderHint="[ Loading NR Branding & Marketing Banner ]"
                  placeholderStyle={{
                    minHeight: "110px",
                    background: "linear-gradient(135deg, #fdfbf7 0%, #f7f1e5 100%)",
                    borderColor: "#d4af37",
                  }}
                  style={{
                    width: "100%",
                    maxHeight: "130px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>

            {/* Sheet 3: Key Features (Part 1 - Features 1 to 5) */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 3 / 5</div>

              {/* Header with Logo */}
              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              <h2
                className="pdf-section-title"
                style={{ color: "#2563eb", marginBottom: "20px" }}
              >
                Key Features
              </h2>

              {/* Features 1 to 5 */}
              {study.detailedFeatures.slice(0, 5).map((feat, idx) => (
                <div className="pdf-feature-card" key={idx}>
                  <span className="pdf-feature-pill-badge">{feat.badge}</span>
                  <p className="pdf-body-paragraph mb-2">{feat.intro}</p>

                  {feat.subheading && (
                    <div
                      className="font-weight-bold mb-2"
                      style={{ fontSize: "0.95rem", color: "#1e293b" }}
                    >
                      {feat.subheading}
                    </div>
                  )}

                  {feat.bullets && feat.bullets.length > 0 && (
                    <ul className="pdf-bullet-list mb-2">
                      {feat.bullets.map((b, bIdx) => (
                        <li key={bIdx}>{b}</li>
                      ))}
                    </ul>
                  )}

                  {feat.outro && (
                    <p
                      className="pdf-body-paragraph mb-0 text-muted"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {feat.outro}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Sheet 4: Key Features (Part 2), Consolidation Matrix & Impact */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 4 / 5</div>

              {/* Header with Logo */}
              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              {/* Features 6 & 7 (Service Menu & Learning Hub) */}
              {study.detailedFeatures.slice(5).map((feat, idx) => (
                <div className="pdf-feature-card mb-3" key={idx}>
                  <span className="pdf-feature-pill-badge">{feat.badge}</span>
                  <p className="pdf-body-paragraph mb-2">{feat.intro}</p>

                  {feat.subheading && (
                    <div
                      className="font-weight-bold mb-2"
                      style={{ fontSize: "0.95rem", color: "#1e293b" }}
                    >
                      {feat.subheading}
                    </div>
                  )}

                  {feat.bullets && feat.bullets.length > 0 && (
                    <ul className="pdf-bullet-list mb-2">
                      {feat.bullets.map((b, bIdx) => (
                        <li key={bIdx}>{b}</li>
                      ))}
                    </ul>
                  )}

                  {feat.outro && (
                    <p
                      className="pdf-body-paragraph mb-0 text-muted"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {feat.outro}
                    </p>
                  )}
                </div>
              ))}

              {/* One Platform. Multiple Needs. (Consolidation Matrix) */}
              <div className="pdf-outlined-frame my-4">
                <div className="pdf-floating-pill">
                  {study.consolidationMatrix.title}
                </div>
                <p className="pdf-frame-intro-text font-weight-bold">
                  {study.consolidationMatrix.intro}
                </p>

                <div className="pdf-matrix-table-wrapper">
                  <table className="pdf-matrix-table">
                    <thead>
                      <tr>
                        {study.consolidationMatrix.columns.map((col, idx) => (
                          <th key={idx}>{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[0, 1, 2, 3].map((rowIdx) => (
                        <tr key={rowIdx}>
                          {study.consolidationMatrix.columns.map(
                            (col, colIdx) => (
                              <td key={colIdx}>{col.items[rowIdx] || "—"}</td>
                            )
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="pdf-frame-outro-text">
                  {study.consolidationMatrix.outro}
                </p>
              </div>

              {/* Impact Section */}
              <div className="mt-3">
                <h2
                  className="pdf-section-title"
                  style={{ color: "#2563eb", marginBottom: "6px" }}
                >
                  {study.impactSection.title}
                </h2>
                <p
                  className="pdf-body-paragraph font-weight-bold mb-1"
                  style={{ color: "#1e293b" }}
                >
                  {study.impactSection.lead}
                </p>
                <p className="pdf-body-paragraph">
                  {study.impactSection.body}
                </p>
              </div>
            </div>

            {/* Sheet 5: Value Combination, The Tedekstra Difference & About */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 5 / 5</div>

              {/* Header with Logo */}
              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              {/* Core Value Combination */}
              <div className="mb-4">
                <p
                  className="pdf-body-paragraph font-weight-bold"
                  style={{ fontSize: "1.05rem", marginBottom: "14px" }}
                >
                  {study.valueCombinationIntro}
                </p>

                <div className="pdf-key-areas-list">
                  {study.valueCombinationPillars.map((pillar, idx) => (
                    <div className="pdf-key-area-pill-row" key={idx}>
                      <span className="pdf-key-area-badge">{pillar.badge}</span>
                      <p className="pdf-key-area-text">{pillar.description}</p>
                    </div>
                  ))}
                </div>

                <p className="pdf-body-paragraph mt-3 text-muted">
                  {study.valueCombinationOutro}
                </p>
              </div>

              {/* The Tedekstra Difference */}
              <div className="pdf-outlined-frame mb-4">
                <div className="pdf-floating-pill">
                  {study.tedekstraDifference.title}
                </div>
                {study.tedekstraDifference.paragraphs.map((p, idx) => (
                  <p key={idx} className="pdf-frame-intro-text mb-2">
                    {p}
                  </p>
                ))}
              </div>

              {/* About Tedekstra & Meeting Visual */}
              <div className="pdf-page4-grid align-items-center">
                <div>
                  <h3 className="pdf-about-bold-title">About Tedekstra</h3>
                  <p className="pdf-body-paragraph">
                    {study.aboutTedekstra[0]}
                  </p>
                  <div className="pdf-about-taglines">
                    <div>{study.aboutTedekstra[1]}</div>
                    <div>{study.aboutTedekstra[2]}</div>
                    <div>{study.aboutTedekstra[3]}</div>
                  </div>
                  <div className="pdf-green-accent-line"></div>
                </div>

                <div>
                  <ImageWithPlaceholder
                    src="/images/case-study-emotions-clinic.jpg"
                    alt="Tedekstra Client Collaboration"
                    placeholderTitle="Client Collaboration Photo"
                    placeholderHint="[ Loading Collaboration Photo ]"
                    iconClass="fa-regular fa-handshake"
                    placeholderStyle={{ minHeight: "220px" }}
                    style={{
                      width: "100%",
                      height: "240px",
                      objectFit: "cover",
                      borderRadius: "10px",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* =========================================================
             STANDARD 4-SHEET PRESENTATION LAYOUT (Emotions Clinic, Pedal Power, etc.)
             ========================================================= */
          <>
            {/* Sheet 1 */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 1 / 4</div>

              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              <div className="pdf-green-hero">
                <div className="row align-items-center">
                  <div className="col-md-7">
                    <div className="pdf-hero-headline">CASE STUDY</div>
                    <div className="pdf-hero-client">{study.client}</div>
                    <div className="pdf-hero-project">{study.subtitle}</div>
                  </div>
                  <div className="col-md-5 mt-3 mt-md-0">
                    <div className="pdf-hero-img-box">
                      <img src={study.image} alt={study.client} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row align-items-center mb-4">
                <div className="col-md-8">
                  <h2 className="pdf-section-title">Client Overview</h2>
                  {study.clientOverview.map((para, idx) => (
                    <p key={idx} className="pdf-body-paragraph">
                      {para}
                    </p>
                  ))}
                </div>
                <div className="col-md-4 d-flex justify-content-md-end justify-content-center mt-3 mt-md-0">
                  <ClientLogo type={study.logoType} />
                </div>
              </div>

              <div className="pdf-outlined-frame">
                <div className="pdf-floating-pill">The Challenge</div>
                <p className="pdf-frame-intro-text">{study.challengeIntro}</p>

                <div className="pdf-2col-chevrons">
                  {study.challenges.map((ch, idx) => (
                    <div className="pdf-chevron-row" key={idx}>
                      <span className="pdf-chevron-circle pdf-circle-green">
                        <i className="fa-solid fa-chevron-right"></i>
                      </span>
                      <span>{ch}</span>
                    </div>
                  ))}
                </div>

                <p className="pdf-frame-outro-text">{study.challengeOutro}</p>
              </div>
            </div>

            {/* Sheet 2 */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 2 / 4</div>

              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              <div className="mb-4">
                <h2 className="pdf-section-title">The Solution</h2>
                <p className="pdf-body-paragraph">{study.solutionIntro}</p>
                <h3
                  className="pdf-section-title"
                  style={{
                    fontSize: "1.1rem",
                    marginTop: "16px",
                    marginBottom: "12px",
                  }}
                >
                  The solution included:
                </h3>

                <div className="pdf-2col-chevrons mb-3">
                  {study.solutionItems.map((item, idx) => (
                    <div className="pdf-chevron-row" key={idx}>
                      <span className="pdf-chevron-circle pdf-circle-coral">
                        <i className="fa-solid fa-chevron-right"></i>
                      </span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <p className="pdf-body-paragraph mt-3">{study.solutionOutro}</p>
              </div>

              <div className="pdf-outlined-frame">
                <div className="pdf-floating-pill">Implementation Approach</div>
                <p className="pdf-frame-intro-text">
                  {study.implementationIntro}
                </p>

                <div className="pdf-4phase-grid">
                  {study.implementationPhases?.map((ph, idx) => (
                    <div className="pdf-phase-box" key={idx}>
                      <span
                        className={`pdf-phase-pill-tag ${
                          ph.color === "pink"
                            ? "pdf-phase-pill-pink"
                            : "pdf-phase-pill-blue"
                        }`}
                      >
                        {ph.phase}
                      </span>
                      <div className="pdf-phase-box-title">{ph.title}</div>
                      <ul className="pdf-bullet-list">
                        {ph.points.map((pt, pIdx) => (
                          <li key={pIdx}>{pt}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sheet 3 */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 3 / 4</div>

              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              <div className="pdf-outlined-frame" style={{ marginTop: "10px" }}>
                <div className="pdf-floating-pill">Business Outcomes</div>
                <p className="pdf-frame-intro-text">{study.outcomesIntro}</p>

                <div className="pdf-outcomes-4grid">
                  {study.outcomesPillars?.map((pillar, idx) => (
                    <div className="pdf-outcome-box" key={idx}>
                      <span className="pdf-outcome-header-pill">
                        {pillar.title}
                      </span>
                      <ul className="pdf-bullet-list">
                        {pillar.points.map((pt, pIdx) => (
                          <li key={pIdx}>{pt}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {study.clientFeedback && (
                <div className="pdf-feedback-metrics-row">
                  <div>
                    <h3 className="pdf-quote-heading">Client Feedback</h3>
                    <p className="pdf-quote-body">
                      "{study.clientFeedback.quote}"
                    </p>
                    <div className="pdf-quote-signoff">
                      {study.clientFeedback.attribution}
                    </div>
                  </div>

                  <div>
                    <h3 className="pdf-table-heading">Key Project Metrics</h3>
                    <table className="pdf-metrics-styled-table">
                      <thead>
                        <tr>
                          <th>Metric</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {study.keyMetricsTable?.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.metric}</td>
                            <td>{row.result}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Sheet 4 */}
            <div className="pdf-page-container">
              <div className="pdf-page-number-indicator">Page 4 / 4</div>

              <div className="pdf-header-row">
                <img
                  src="/images/logo.png"
                  alt="Tedekstra"
                  style={{ height: "38px" }}
                />
              </div>

              <div className="pdf-page4-grid">
                <div className="pdf-outlined-frame" style={{ margin: 0 }}>
                  <div className="pdf-floating-pill">Looking Ahead</div>
                  {study.lookingAhead?.map((para, idx) => (
                    <p key={idx} className="pdf-body-paragraph">
                      {para}
                    </p>
                  ))}
                </div>

                <div>
                  <h3 className="pdf-about-bold-title">About Tedekstra</h3>
                  <p className="pdf-body-paragraph">
                    {study.aboutTedekstra?.[0]}
                  </p>
                  <div className="pdf-about-taglines">
                    <div>{study.aboutTedekstra?.[1]}</div>
                    <div>{study.aboutTedekstra?.[2]}</div>
                    <div>{study.aboutTedekstra?.[3]}</div>
                  </div>
                  <div className="pdf-green-accent-line"></div>
                </div>
              </div>

              <div className="pdf-bottom-photo-banner">
                <img
                  src={study.image || "/images/case-study-emotions-clinic.jpg"}
                  alt={`${study.client} Partnership`}
                />
              </div>
            </div>
          </>
        )}

        {/* Bottom Action Buttons */}
        <div className="pdf-bottom-action-buttons">
          <Link
            to="/case-studies"
            className="btn btn-dark rounded-pill px-4 py-2"
            style={{ background: "#121417", marginRight: "16px" }}
          >
            <i className="fa-solid fa-arrow-left me-2"></i> Back to All Case
            Studies
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="btn btn-outline-dark rounded-pill px-4 py-2"
            style={{ marginRight: "14px" }}
          >
            <i className="fa-solid fa-print me-2"></i> Print / Save as PDF
          </button>
          <a
            href={study.pdfUrl}
            download={`${study.slug}-case-study.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-danger rounded-pill px-4 py-2"
          >
            <i className="fa-regular fa-file-pdf me-2"></i> Download Official PDF
          </a>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyDetail;
